import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import CampaignPackageService from 'services/campaignPackage';
import { CAMPAIGN_PACKAGE_SLOT_SELECTION } from 'services/campaignPackage/constant';
import { Promotion } from './types';

interface PromotionState {
  // Separate data for each type
  slotPromotions: Promotion[];
  servicePromotions: Promotion[];

  // Combined list for backward compatibility
  promotions: Promotion[];
  activePromotions: Promotion[];
  selectedPromotion: Promotion | null;
  isLoading: boolean;
  error: string | null;

  // Pagination for each type
  slotPage: number;
  slotTotal: number;
  slotHasMore: boolean;

  servicePage: number;
  serviceTotal: number;
  serviceHasMore: boolean;

  limit: number;
  isLoadingMore: boolean;

  // Actions
  loadPromotions: (shopId: string, type?: 'slot' | 'service', reset?: boolean) => Promise<void>;
  loadMorePromotions: (shopId: string, type?: 'slot' | 'service') => Promise<void>;
  createPromotion: (data: any) => Promise<any>;
  updatePromotion: (id: string, data: Partial<Promotion>) => Promise<void>;
  updatePromotionStatus: (id: string, status: string) => Promise<any>;
  deletePromotion: (id: string) => Promise<void>;
  activatePromotion: (id: string) => Promise<void>;
  deactivatePromotion: (id: string) => Promise<void>;
  setSelectedPromotion: (promotion: Promotion | null) => void;
  clearError: () => void;
  resetPromotions: () => void;
}

export const usePromotionStore = create<PromotionState>()(
  devtools(
    (set, get) => ({
      slotPromotions: [],
      servicePromotions: [],
      promotions: [],
      activePromotions: [],
      selectedPromotion: null,
      isLoading: false,
      error: null,

      slotPage: 1,
      slotTotal: 0,
      slotHasMore: true,

      servicePage: 1,
      serviceTotal: 0,
      serviceHasMore: true,

      limit: 10,
      isLoadingMore: false,

      loadPromotions: async (shopId, type, reset = true) => {
        if (reset) {
          set({ isLoading: true, error: null });

          // Reset pagination for the specific type
          if (type === 'slot') {
            set({ slotPage: 1 });
          } else if (type === 'service') {
            set({ servicePage: 1 });
          }
        }

        try {
          const query: any = {
            where: {
              shop: { equals: shopId },
            },
            page: 1,
            limit: get().limit,
            orderBy: { createdAt: 'desc' },
            fetchPolicy: reset ? 'network-only' : 'cache-first',
          };

          if (type) {
            query.where.type = { equals: type };
          }

          const response = await CampaignPackageService.find(
            query,
            type === 'slot' ? CAMPAIGN_PACKAGE_SLOT_SELECTION : undefined
          );

          const newPromotions = (response?.data || []) as Promotion[];
          const total = response?.params?.total || 0;

          // Calculate promotionStatus for each item
          const promotionsWithStatus = newPromotions.map((promo: any) => ({
            ...promo,
            promotionStatus: CampaignPackageService.calculatePromotionStatus(promo)
          }));

          const now = new Date();

          if (type === 'slot') {
            set({
              slotPromotions: promotionsWithStatus,
              slotTotal: total,
              slotHasMore: promotionsWithStatus.length < total,
              slotPage: 1,
              isLoading: false,
            });
          } else if (type === 'service') {
            set({
              servicePromotions: promotionsWithStatus,
              serviceTotal: total,
              serviceHasMore: promotionsWithStatus.length < total,
              servicePage: 1,
              isLoading: false,
            });
          } else {
            // If no type specified, load all and update combined list
            const activePromotions = promotionsWithStatus.filter(
              (promo: any) =>
                promo.status === 'available' &&
                new Date(promo.startDate) <= now &&
                new Date(promo.endDate) >= now
            );

            set({
              promotions: promotionsWithStatus,
              activePromotions,
              isLoading: false,
            });
          }
        } catch (error: any) {
          set({
            error: error.message || 'Failed to load promotions',
            isLoading: false,
          });
          console.error('Error loading promotions:', error);
        }
      },

      loadMorePromotions: async (shopId, type) => {
        const state = get();

        // Check has more based on type
        let hasMore, page, currentPromotions;
        if (type === 'slot') {
          hasMore = state.slotHasMore;
          page = state.slotPage;
          currentPromotions = state.slotPromotions;
        } else if (type === 'service') {
          hasMore = state.serviceHasMore;
          page = state.servicePage;
          currentPromotions = state.servicePromotions;
        } else {
          return; // Type is required for load more
        }

        if (!hasMore || state.isLoadingMore) return;

        set({ isLoadingMore: true, error: null });

        try {
          const nextPage = page + 1;
          const query: any = {
            where: {
              shop: { equals: shopId },
              type: { equals: type },
            },
            page: nextPage,
            limit: state.limit,
            orderBy: { createdAt: 'desc' },
          };

          const response = await CampaignPackageService.find(
            query,
            type === 'slot' ? CAMPAIGN_PACKAGE_SLOT_SELECTION : undefined
          );

          const newPromotions = (response?.data || []) as Promotion[];
          const total = response?.params?.total || 0;

          // Calculate promotionStatus for each item
          const promotionsWithStatus = newPromotions.map((promo: any) => ({
            ...promo,
            promotionStatus: CampaignPackageService.calculatePromotionStatus(promo)
          }));

          const updatedPromotions = [...currentPromotions, ...promotionsWithStatus];

          if (type === 'slot') {
            set({
              slotPromotions: updatedPromotions,
              slotTotal: total,
              slotHasMore: updatedPromotions.length < total,
              slotPage: nextPage,
              isLoadingMore: false,
            });
          } else if (type === 'service') {
            set({
              servicePromotions: updatedPromotions,
              serviceTotal: total,
              serviceHasMore: updatedPromotions.length < total,
              servicePage: nextPage,
              isLoadingMore: false,
            });
          }
        } catch (error: any) {
          set({
            error: error.message || 'Failed to load more promotions',
            isLoadingMore: false,
          });
          console.error('Error loading more promotions:', error);
        }
      },

      createPromotion: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await CampaignPackageService.create(data);
          const newPromotion = response?.data;

          if (newPromotion) {
            // Add promotionStatus to the new item
            newPromotion.promotionStatus = CampaignPackageService.calculatePromotionStatus(newPromotion);

            set((state) => {
              // Add to the appropriate list based on type
              if (newPromotion.type === 'slot') {
                return {
                  slotPromotions: [newPromotion, ...state.slotPromotions],
                  slotTotal: state.slotTotal + 1,
                  isLoading: false,
                };
              } else if (newPromotion.type === 'service') {
                return {
                  servicePromotions: [newPromotion, ...state.servicePromotions],
                  serviceTotal: state.serviceTotal + 1,
                  isLoading: false,
                };
              }

              // Fallback to combined list
              const promotions = [newPromotion, ...state.promotions];
              const now = new Date();
              const activePromotions = promotions.filter(
                (promo: any) =>
                  promo.status === 'available' &&
                  new Date(promo.startDate) <= now &&
                  new Date(promo.endDate) >= now
              );

              return {
                promotions,
                activePromotions,
                isLoading: false,
              };
            });
          }
          return response;
        } catch (error: any) {
          set({
            error: error.message || 'Failed to create promotion',
            isLoading: false,
          });
          throw error;
        }
      },

      updatePromotion: async (id, data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await CampaignPackageService.update({
            id,
            data,
          });
          const updatedPromotion = response?.data as Promotion;

          if (updatedPromotion) {
            set((state) => {
              const promotions = state.promotions.map((promo) =>
                promo.id === id ? updatedPromotion : promo
              );
              const now = new Date();
              const activePromotions = promotions.filter(
                (promo) =>
                  promo.isActive &&
                  new Date(promo.startDate) <= now &&
                  new Date(promo.endDate) >= now
              );

              return {
                promotions,
                activePromotions,
                selectedPromotion:
                  state.selectedPromotion?.id === id
                    ? updatedPromotion
                    : state.selectedPromotion,
                isLoading: false,
              };
            });
          }
        } catch (error: any) {
          set({
            error: error.message || 'Failed to update promotion',
            isLoading: false,
          });
          throw error;
        }
      },

      deletePromotion: async (id) => {
        set({ isLoading: true, error: null });
        try {
          await CampaignPackageService.delete({ id });

          set((state) => {
            const promotions = state.promotions.filter(
              (promo) => promo.id !== id
            );
            const activePromotions = state.activePromotions.filter(
              (promo) => promo.id !== id
            );

            return {
              promotions,
              activePromotions,
              selectedPromotion:
                state.selectedPromotion?.id === id
                  ? null
                  : state.selectedPromotion,
              isLoading: false,
            };
          });
        } catch (error: any) {
          set({
            error: error.message || 'Failed to delete promotion',
            isLoading: false,
          });
          throw error;
        }
      },

      updatePromotionStatus: async (id, status) => {
        try {
          const result = await CampaignPackageService.updatePromotionStatus(id, status);
          if (result && !result.errors && result.data) {
            // Update the item in the store
            set((state) => {
              // Update in both slot and service lists
              const slotPromotions = state.slotPromotions.map((promo: any) =>
                promo.id === id
                  ? { ...promo, ...result.data, status, promotionStatus: result.data.promotionStatus }
                  : promo
              );

              const servicePromotions = state.servicePromotions.map((promo: any) =>
                promo.id === id
                  ? { ...promo, ...result.data, status, promotionStatus: result.data.promotionStatus }
                  : promo
              );

              // Also update combined list if it exists
              const promotions = state.promotions.map((promo: any) =>
                promo.id === id
                  ? { ...promo, ...result.data, status, promotionStatus: result.data.promotionStatus }
                  : promo
              );

              const now = new Date();
              const activePromotions = promotions.filter(
                (promo: any) =>
                  promo.status === 'available' &&
                  new Date(promo.startDate) <= now &&
                  new Date(promo.endDate) >= now
              );

              return {
                slotPromotions,
                servicePromotions,
                promotions,
                activePromotions,
              };
            });
            return result;
          }
          return result || { errors: [{ message: 'Unknown error' }] };
        } catch (error: any) {
          console.error('Error updating promotion status:', error);
          throw error;
        }
      },

      activatePromotion: async (id) => {
        await get().updatePromotionStatus(id, 'available');
      },

      deactivatePromotion: async (id) => {
        await get().updatePromotionStatus(id, 'waiting');
      },

      setSelectedPromotion: (promotion) => {
        set({ selectedPromotion: promotion });
      },

      clearError: () => {
        set({ error: null });
      },

      resetPromotions: () => {
        set({
          slotPromotions: [],
          servicePromotions: [],
          promotions: [],
          activePromotions: [],
          selectedPromotion: null,
          isLoading: false,
          error: null,
          slotPage: 1,
          slotTotal: 0,
          slotHasMore: true,
          servicePage: 1,
          serviceTotal: 0,
          serviceHasMore: true,
          isLoadingMore: false,
        });
      },
    }),
    { name: 'promotion-store' }
  )
);